import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  emptyAddress,
  nominations,
  settings,
  type Address,
  type DocumentKind,
  type Policyholder,
  type PolicyholderInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { cnpjCompleto, dinheiroParaCampo } from '@/shared/lib/consultaCnpj';
import {
  formatCnae,
  moneyInputError,
  moneyToInput,
  parseRate,
  rateInputError,
  rateToInput,
  toMoney,
  zeroMoney,
} from '@/shared/lib/format';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { AvisoDaConsultaCnpj } from '@/shared/ui/AvisoDaConsultaCnpj';
import { Button } from '@/shared/ui/Button';
import { CnaePicker } from '@/shared/ui/CnaePicker';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useConsultaCnpj } from '@/shared/ui/useConsultaCnpj';

import { fieldErrorOf } from '../shared/fieldError';
import { PERMISSAO_DA_CONSULTA_NO_CADASTRO } from './consultaNoCadastro';
import {
  dicaCredito,
  FUNCIONALIDADE_PESSOA_FISICA,
  DICA_CORRETOR_POR_PEDIDO,
  instrumentosDeNomeacao,
  permissaoCredito,
  situacoesDeCredito,
  situacoesDoCadastro,
  situacoesNaReceita,
  tiposDePessoa,
} from './rotulos';
import { camposDoTomadorDaReceita, type CampoDoTomador } from './receitaDoTomador';

const marcaTriplice = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Nao' },
];

function marcaParaTexto(valor: boolean | null | undefined): string {
  if (valor === true) return 'sim';
  if (valor === false) return 'nao';
  return '';
}

function textoParaMarca(valor: string): boolean | null {
  if (valor === 'sim') return true;
  if (valor === 'nao') return false;
  return null;
}

function Marcador({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (valor: boolean) => void;
  hint?: string | undefined;
}) {
  return (
    <label className="flex items-start gap-2 text-xs font-semibold text-ink-700">
      <input
        type="checkbox"
        className="mt-0.5 size-4 rounded border-line-strong"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        {label}
        {hint ? <span className="block font-normal text-ink-500">{hint}</span> : null}
      </span>
    </label>
  );
}

function Grupo({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-t border-line pt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
        {titulo}
      </h3>
      {children}
    </section>
  );
}

/**
 * Cadastro do tomador, com a ficha completa.
 *
 * No portal da corretora, situacao, limite, comercial responsavel, corretor e o que
 * a seguradora decide na ficha (comissao, nomeacao, credito, CCG e indicadores) nao
 * aparecem: o servidor os ignora no pedido do parceiro. Da ficha, o parceiro
 * descreve a empresa.
 *
 * Na seguradora, limite, situacao, situacao de credito, revisao automatica do limite
 * e aprovacao forcada tem alcada propria (tomadores.credito.update). Sem ela, ficam
 * travados no valor gravado, e o formulario os devolve como estao.
 *
 * P-TOM-C1: o documento e CPF ou CNPJ (o tomador pessoa fisica so aparece quando a
 * empresa tem a funcionalidade), o CNAE vem da CNAE do IBGE, o municipio vai com o
 * codigo IBGE e a negativa de credito leva o motivo.
 *
 * P-NCT-3: na pessoa fisica o nome e o "Nome completo", o telefone e obrigatorio (como
 * no cadastro da pessoa fisica do OnPoint, NCT-190) e os campos que so descrevem
 * empresa (porte, natureza juridica, CNAE, capital social, data de abertura, situacao
 * na Receita e a ficha da empresa) nao aparecem: o servidor tambem os ignora
 * (minimizacao de dado pessoal).
 *
 * Consulta de CNPJ (ADR 0092): na pessoa juridica, ao completar um CNPJ valido (ou em
 * "Buscar dados na Receita"), os dados da Receita Federal pela BrasilAPI preenchem os
 * campos vazios. O que ja tem valor fica como esta, e a tela pergunta antes de trocar.
 * A Situacao (do tomador na plataforma) nunca muda pela consulta.
 *
 * Consulta no cadastro (ADR 0102): no cadastro novo de CNPJ, o botao Consultar espera os
 * dados da Receita e entrega o formulario a `onConsultar`, que grava o tomador e abre a
 * analise. So CNPJ: o motor nao roda para CPF.
 */
export function PolicyholderForm({
  holder,
  readOnly,
  submitting,
  error,
  onSubmit,
  onCancel,
  onConsultar,
  consultando,
}: {
  holder?: Policyholder | undefined;
  readOnly?: boolean | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: PolicyholderInput) => void;
  onCancel: () => void;
  onConsultar?: ((input: PolicyholderInput) => void) | undefined;
  consultando?: boolean | undefined;
}) {
  const { session, can, hasFeature } = useAuth();
  const daCorretora = session?.broker_scoped === true;
  const podeCredito = can(permissaoCredito);
  const aceitaPessoaFisica = hasFeature(FUNCIONALIDADE_PESSOA_FISICA);
  const [legalName, setLegalName] = useState(holder?.legal_name ?? '');
  const [tradeName, setTradeName] = useState(holder?.trade_name ?? '');
  const [document, setDocument] = useState(holder?.document ?? '');
  const [documentKind, setDocumentKind] = useState<DocumentKind>(
    holder?.document_kind ?? 'cnpj',
  );
  const [registration, setRegistration] = useState(holder?.state_registration ?? '');
  const [status, setStatus] = useState(holder?.status ?? 'ativo');
  const [email, setEmail] = useState(holder?.email ?? '');
  const [phone, setPhone] = useState(holder?.phone ?? '');
  const [contactName, setContactName] = useState(holder?.contact_name ?? '');
  const [limite, setLimite] = useState(moneyToInput(holder?.credit_limit));
  // O valor que a tela abriu, que nao muda se o tomador for lido de novo. Na edicao, limite e
  // situacao so vao ao servidor se a pessoa os mudou, com esse valor de base: quem abriu a
  // tela antes do motor aprovar nao apaga a aprovacao sem saber (ADR 0102).
  const [limiteAoAbrir] = useState(holder?.credit_limit ?? zeroMoney());
  const [notes, setNotes] = useState(holder?.notes ?? '');
  const [address, setAddress] = useState<Address>({
    ...emptyAddress,
    city_code: '',
    ...holder?.address,
  });
  const [companySize, setCompanySize] = useState(holder?.company_size ?? '');
  const [legalNature, setLegalNature] = useState(holder?.legal_nature ?? '');
  const [cnae, setCnae] = useState(holder?.cnae ?? '');
  const [shareCapital, setShareCapital] = useState(moneyToInput(holder?.share_capital));
  const [foundedOn, setFoundedOn] = useState(holder?.founded_on ?? '');
  const [brokerId, setBrokerId] = useState(holder?.broker_id ?? '');
  const [commercialOwner, setCommercialOwner] = useState(
    holder?.commercial_owner ?? '',
  );

  // Ficha completa.
  const [validadeCadastral, setValidadeCadastral] = useState(
    holder?.registration_valid_until ?? '',
  );
  const [recadastro, setRecadastro] = useState(holder?.reregistered_on ?? '');
  const [comissao, setComissao] = useState(
    holder?.commission_bps === null || holder?.commission_bps === undefined
      ? ''
      : rateToInput(holder.commission_bps),
  );
  const [validadeNomeacao, setValidadeNomeacao] = useState(
    holder?.nomination_valid_until ?? '',
  );
  const [instrumento, setInstrumento] = useState(holder?.nomination_instrument ?? '');
  const [obsFundacao, setObsFundacao] = useState(holder?.foundation_note ?? '');
  const [receita, setReceita] = useState(holder?.registration_situation ?? '');
  const [descricaoCnae, setDescricaoCnae] = useState(holder?.cnae_description ?? '');
  const [secundarios, setSecundarios] = useState<string[]>(
    holder?.secondary_cnaes ?? [],
  );
  const [ramo, setRamo] = useState(holder?.branch_activity ?? '');
  const [ramoRestrito, setRamoRestrito] = useState(
    marcaParaTexto(holder?.branch_activity_restricted),
  );
  const [situacaoCredito, setSituacaoCredito] = useState(holder?.credit_status ?? '');
  const [situacaoAoAbrir] = useState(holder?.credit_status ?? '');
  const [motivoAoAbrir] = useState(holder?.credit_denial_reason ?? '');
  const [padronizadoAoAbrir] = useState(holder?.credit_denial_reason_id ?? '');
  const [parcelamentoAnual, setParcelamentoAnual] = useState(
    holder?.enable_annual_installments ?? false,
  );
  const [revisaoAutomatica, setRevisaoAutomatica] = useState(
    holder?.limit_automatic_review ?? false,
  );
  const [classePagamento, setClassePagamento] = useState(
    holder?.payment_equivalent_class ?? '',
  );
  const [aprovacaoForcada, setAprovacaoForcada] = useState(
    holder?.force_approved_dirty_list ?? false,
  );
  const [ultimaSinistros, setUltimaSinistros] = useState(
    holder?.last_claims_evaluation_on ?? '',
  );
  const [percentualFaturamento, setPercentualFaturamento] = useState(
    holder?.annual_billing_percentage_bps === null ||
      holder?.annual_billing_percentage_bps === undefined
      ? ''
      : rateToInput(holder.annual_billing_percentage_bps),
  );
  const [clausulaCcg, setClausulaCcg] = useState(holder?.ccg_custom_clause ?? '');
  const [usaConjuge, setUsaConjuge] = useState(holder?.ccg_uses_spouse ?? false);
  const [direitoPrivado, setDireitoPrivado] = useState(
    marcaParaTexto(holder?.is_private),
  );
  const [motivoNegativa, setMotivoNegativa] = useState(
    holder?.credit_denial_reason ?? '',
  );
  const [motivoPadronizado, setMotivoPadronizado] = useState(
    holder?.credit_denial_reason_id ?? '',
  );

  // Consulta de CNPJ pela BrasilAPI: preenche so o vazio e guarda o diferente para a
  // pessoa decidir. "Cadastro na Receita" so entra onde o campo aparece.
  const consultaCnpj = useConsultaCnpj<CampoDoTomador>({
    planejar: (r) =>
      camposDoTomadorDaReceita(
        {
          legal_name: legalName,
          trade_name: tradeName,
          registration_situation: receita,
          legal_nature: legalNature,
          cnae,
          cnae_description: descricaoCnae,
          secondary_cnaes: secundarios,
          share_capital: shareCapital,
          company_size: companySize,
          founded_on: foundedOn,
          email,
          phone,
          address,
        },
        r,
        { comCadastroNaReceita: !daCorretora },
      ),
    aplicar: (chaves, r) => {
      for (const chave of chaves) {
        switch (chave) {
          case 'legal_name':
            setLegalName(r.legal_name);
            break;
          case 'trade_name':
            setTradeName(r.trade_name);
            break;
          case 'registration_situation':
            setReceita(r.registration_situation);
            break;
          case 'legal_nature':
            setLegalNature(r.legal_nature);
            break;
          case 'cnae':
            setCnae(r.cnae);
            setDescricaoCnae(r.cnae_description);
            break;
          case 'secondary_cnaes':
            setSecundarios(r.secondary_cnaes.map((item) => item.code));
            break;
          case 'share_capital':
            setShareCapital(dinheiroParaCampo(r.share_capital));
            break;
          case 'company_size':
            setCompanySize(r.company_size);
            break;
          case 'founded_on':
            setFoundedOn(r.founded_on ?? '');
            break;
          case 'email':
            setEmail(r.email);
            break;
          case 'phone':
            setPhone(r.phone);
            break;
          case 'address':
            setAddress({ ...emptyAddress, ...r.address });
            break;
        }
      }
    },
  });

  // Motivos padronizados de credito, para a negativa (Configuracoes).
  const motivosDeCredito = useQuery({
    queryKey: ['decline-reasons', 'credito'],
    queryFn: ({ signal }) => settings.declineReasons({ scope: 'credito' }, signal),
    enabled: !daCorretora && podeCredito && situacaoCredito === 'reprovado',
    staleTime: 5 * 60_000,
  });

  // Apenas corretores ativos podem ser vinculados: o mesmo criterio que a operacao
  // aplica, e que o servidor confere.
  const listaCorretores = useQuery({
    queryKey: ['brokers', 'options'],
    queryFn: ({ signal }) => brokers.list({ limit: 100, stage: 'ativo' }, signal),
    enabled: !daCorretora,
  });

  // Na empresa que troca o corretor por pedido (P-TOM-C5), a edicao nao troca o
  // corretor ja vinculado: a troca e a aprovacao do pedido, na secao Corretor. O
  // cadastro novo e o tomador sem corretor recebem o primeiro aqui.
  const modoNomeacao = useQuery({
    queryKey: ['nomination-settings'],
    queryFn: ({ signal }) => nominations.settings(signal),
    enabled: !daCorretora && holder !== undefined,
    staleTime: 5 * 60_000,
  });
  const corretorPorPedido =
    holder !== undefined &&
    Boolean(holder.broker_id) &&
    modoNomeacao.data?.mode === 'pedido';

  const apiError = error instanceof ApiError ? error : null;
  const erroLimite = moneyInputError(limite);
  const erroCapital = moneyInputError(shareCapital);
  const erroComissao = rateInputError(comissao);
  const erroPercentual = rateInputError(percentualFaturamento);
  const valorInvalido =
    erroLimite !== undefined ||
    erroCapital !== undefined ||
    erroComissao !== undefined ||
    erroPercentual !== undefined;

  function creditoDaEntrada(): Partial<PolicyholderInput> {
    const limiteDigitado = limite ? toMoney(limite) : zeroMoney();
    const negado = situacaoCredito === 'reprovado';
    // O motivo so vale com a negativa, e so anda junto com a situacao; fora dela, o servidor
    // o tira.
    const situacao = {
      credit_status: situacaoCredito,
      credit_denial_reason: negado ? motivoNegativa : '',
      credit_denial_reason_id: negado && motivoPadronizado ? motivoPadronizado : null,
    };
    if (!holder) {
      return { credit_limit: limiteDigitado, ...situacao };
    }
    const situacaoMudou =
      situacaoCredito !== situacaoAoAbrir ||
      motivoNegativa !== motivoAoAbrir ||
      motivoPadronizado !== padronizadoAoAbrir;
    return {
      ...(limiteDigitado.amount !== limiteAoAbrir.amount
        ? { credit_limit: limiteDigitado, credit_limit_base: limiteAoAbrir }
        : {}),
      ...(situacaoMudou ? { ...situacao, credit_status_base: situacaoAoAbrir } : {}),
    };
  }

  function entradaDoFormulario(): PolicyholderInput {
    return {
      ...creditoDaEntrada(),
      legal_name: legalName,
      trade_name: tradeName,
      document,
      document_kind: documentKind,
      state_registration: registration,
      status,
      email,
      phone,
      contact_name: contactName,
      notes,
      address,
      company_size: companySize,
      legal_nature: legalNature,
      cnae,
      share_capital: shareCapital ? toMoney(shareCapital) : zeroMoney(),
      founded_on: foundedOn,
      broker_id: brokerId,
      commercial_owner: commercialOwner,

      registration_valid_until: validadeCadastral,
      reregistered_on: recadastro,
      commission_bps: comissao.trim() === '' ? null : parseRate(comissao),
      nomination_valid_until: validadeNomeacao,
      nomination_instrument: instrumento,
      foundation_note: obsFundacao,
      registration_situation: receita,
      cnae_description: descricaoCnae,
      secondary_cnaes: secundarios,
      branch_activity: ramo,
      branch_activity_restricted: textoParaMarca(ramoRestrito),
      enable_annual_installments: parcelamentoAnual,
      limit_automatic_review: revisaoAutomatica,
      payment_equivalent_class: classePagamento,
      force_approved_dirty_list: aprovacaoForcada,
      last_claims_evaluation_on: ultimaSinistros,
      annual_billing_percentage_bps:
        percentualFaturamento.trim() === '' ? null : parseRate(percentualFaturamento),
      ccg_custom_clause: clausulaCcg,
      ccg_uses_spouse: usaConjuge,
      is_private: textoParaMarca(direitoPrivado),
    };
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (valorInvalido) return;
    onSubmit(entradaDoFormulario());
  }

  // Consulta no cadastro: o clique espera a Receita, e so entao entrega o formulario, ja
  // com os campos preenchidos. A falha da Receita nao trava: com a razao social digitada,
  // a consulta sai do mesmo jeito.
  const esperandoReceita = useRef(false);
  const [erroDaConsulta, setErroDaConsulta] = useState('');
  const entregar = useRef<() => void>(() => undefined);
  useEffect(() => {
    entregar.current = () => {
      if (legalName.trim() === '') {
        setErroDaConsulta(
          'Nao veio a razao social da Receita. Preencha o campo e consulte de novo.',
        );
        return;
      }
      if (!valorInvalido) onConsultar?.(entradaDoFormulario());
    };
  });
  const situacaoDaReceita = consultaCnpj.situacao.tipo;
  useEffect(() => {
    if (!esperandoReceita.current || situacaoDaReceita === 'consultando') return;
    esperandoReceita.current = false;
    entregar.current();
  }, [situacaoDaReceita]);

  function pedirConsulta() {
    setErroDaConsulta('');
    const cnpj = cnpjCompleto(document);
    if (!cnpj) return;
    const receita = consultaCnpj.situacao;
    if (receita.tipo === 'pronta' && receita.cnpj === cnpj) {
      entregar.current();
      return;
    }
    esperandoReceita.current = true;
    if (receita.tipo !== 'consultando') consultaCnpj.consultar(document);
  }

  // O tipo do documento muda o rotulo e a conferencia. Sem a funcionalidade, a
  // pessoa fisica so aparece no cadastro que ja e pessoa fisica.
  const opcoesDeTipo = tiposDePessoa.filter(
    (tipo) => tipo.value === 'cnpj' || aceitaPessoaFisica || documentKind === 'cpf',
  );
  const documentoTravado = daCorretora && holder !== undefined;
  const pessoaFisica = documentKind === 'cpf';
  const mostraConsulta =
    onConsultar !== undefined &&
    holder === undefined &&
    documentKind === 'cnpj' &&
    can(PERMISSAO_DA_CONSULTA_NO_CADASTRO);

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {error.message}
        </Alert>
      ) : null}

      <fieldset disabled={readOnly || submitting} className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          {pessoaFisica ? (
            <Field
              label="Nome completo"
              required
              value={legalName}
              error={fieldErrorOf(apiError, 'legal_name')}
              onChange={(event) => setLegalName(event.target.value)}
            />
          ) : (
            <Field
              label="Razao social"
              required
              value={legalName}
              error={fieldErrorOf(apiError, 'legal_name')}
              onChange={(event) => setLegalName(event.target.value)}
            />
          )}
          <Field
            label="Nome fantasia"
            value={tradeName}
            onChange={(event) => setTradeName(event.target.value)}
          />
          <Select
            label="Tipo de pessoa"
            options={opcoesDeTipo}
            value={documentKind}
            disabled={documentoTravado}
            error={fieldErrorOf(apiError, 'document_kind')}
            hint={
              aceitaPessoaFisica
                ? undefined
                : 'A empresa nao tem o cadastro de tomador pessoa fisica habilitado.'
            }
            onChange={(event) => {
              const tipo = event.target.value as DocumentKind;
              setDocumentKind(tipo);
              // A consulta e so do CNPJ: na pessoa fisica, o aviso some.
              consultaCnpj.acompanhar(tipo === 'cnpj' ? document : '');
            }}
          />
          <div className="flex flex-col gap-1.5">
            <Field
              label="CPF/CNPJ"
              required
              placeholder={
                documentKind === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'
              }
              value={document}
              disabled={documentoTravado}
              error={fieldErrorOf(apiError, 'document')}
              onChange={(event) => {
                setDocument(event.target.value);
                if (documentKind === 'cnpj')
                  consultaCnpj.acompanhar(event.target.value);
              }}
            />
            {documentKind === 'cnpj' && !readOnly ? (
              <Button
                variant="ghost"
                size="sm"
                className="self-start"
                loading={consultaCnpj.situacao.tipo === 'consultando'}
                disabled={cnpjCompleto(document) === null}
                onClick={() => consultaCnpj.consultar(document)}
              >
                Buscar dados na Receita
              </Button>
            ) : null}
            {mostraConsulta ? (
              <>
                <Button
                  size="sm"
                  className="self-start"
                  loading={situacaoDaReceita === 'consultando' || consultando === true}
                  disabled={cnpjCompleto(document) === null}
                  onClick={pedirConsulta}
                >
                  Consultar
                </Button>
                <p className="text-2xs text-ink-500">
                  {daCorretora
                    ? 'Grava o tomador e analisa o credito. Cada consulta nova conta no limite mensal da corretora.'
                    : 'Grava o tomador e consulta o bureau, que pode cobrar pela consulta. Consulta valida recente e reaproveitada, sem nova cobranca.'}
                </p>
                {erroDaConsulta ? (
                  <p role="alert" className="text-xs text-danger-700">
                    {erroDaConsulta}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
          <Field
            label="Inscricao estadual"
            value={registration}
            onChange={(event) => setRegistration(event.target.value)}
          />
        </div>

        {documentKind === 'cnpj' ? (
          <AvisoDaConsultaCnpj consulta={consultaCnpj} />
        ) : null}

        {!daCorretora ? (
          <fieldset
            disabled={!podeCredito}
            className="grid gap-4 sm:grid-cols-2"
            aria-label="Credito do tomador"
          >
            {!podeCredito ? (
              <p className="text-2xs text-ink-500 sm:col-span-2">{dicaCredito}</p>
            ) : null}
            <Select
              label="Situacao"
              options={situacoesDoCadastro}
              value={status}
              error={fieldErrorOf(apiError, 'status')}
              hint="Tomador bloqueado permanece consultavel e nao recebe operacao nova."
              onChange={(event) => setStatus(event.target.value)}
            />
            <MoneyField
              label="Limite de credito aprovado (R$)"
              value={limite}
              error={erroLimite ?? fieldErrorOf(apiError, 'credit_limit')}
              hint="Vazio significa sem limite aprovado."
              onValueChange={setLimite}
            />
            <Select
              label="Status de credito"
              value={situacaoCredito}
              placeholder="Nao decidido"
              options={situacoesDeCredito}
              error={fieldErrorOf(apiError, 'credit_status')}
              onChange={(event) => setSituacaoCredito(event.target.value)}
            />
            <div className="flex flex-col gap-2">
              <Marcador
                label="Revisao automatica do limite"
                checked={revisaoAutomatica}
                onChange={setRevisaoAutomatica}
              />
              <Marcador
                label="Aprovacao forcada apesar de lista restritiva"
                checked={aprovacaoForcada}
                onChange={setAprovacaoForcada}
              />
            </div>
            {situacaoCredito === 'reprovado' ? (
              <>
                <Select
                  label="Motivo padronizado da negativa"
                  placeholder="Sem motivo padronizado"
                  options={(motivosDeCredito.data ?? []).map((motivo) => ({
                    value: motivo.id,
                    label: motivo.label,
                  }))}
                  value={motivoPadronizado}
                  error={fieldErrorOf(apiError, 'credit_denial_reason_id')}
                  onChange={(event) => {
                    const escolhido = motivosDeCredito.data?.find(
                      (motivo) => motivo.id === event.target.value,
                    );
                    setMotivoPadronizado(event.target.value);
                    if (escolhido && motivoNegativa.trim() === '') {
                      setMotivoNegativa(escolhido.label);
                    }
                  }}
                />
                <Textarea
                  label="Motivo da negativa"
                  value={motivoNegativa}
                  error={fieldErrorOf(apiError, 'credit_denial_reason')}
                  hint="Registro da negativa. Nao decide nada sozinho: a negativa e a situacao de credito."
                  onChange={(event) => setMotivoNegativa(event.target.value)}
                />
              </>
            ) : null}
          </fieldset>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Contato"
            value={contactName}
            onChange={(event) => setContactName(event.target.value)}
          />
          <Field
            label="Email"
            type="email"
            value={email}
            error={fieldErrorOf(apiError, 'email')}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            label="Telefone"
            required={pessoaFisica}
            value={phone}
            error={fieldErrorOf(apiError, 'phone')}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>

        {/* Perfil da empresa. E o que a subscricao olha para dimensionar risco:
            sem isso, quem analisa precisa procurar fora do sistema. Na pessoa fisica
            nao se aplica (P-NCT-3, minimizacao). */}
        {!pessoaFisica ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Porte"
              value={companySize}
              placeholder="Nao informado"
              options={[
                { value: 'mei', label: 'MEI' },
                { value: 'me', label: 'Microempresa' },
                { value: 'epp', label: 'Empresa de pequeno porte' },
                { value: 'medio', label: 'Medio porte' },
                { value: 'grande', label: 'Grande porte' },
              ]}
              onChange={(event) => setCompanySize(event.target.value)}
            />
            <Field
              label="Natureza juridica"
              value={legalNature}
              onChange={(event) => setLegalNature(event.target.value)}
            />
          </div>
        ) : null}

        {!pessoaFisica ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <CnaePicker
              label="Atividade principal (CNAE)"
              value={cnae}
              description={descricaoCnae}
              clearLabel="Limpar CNAE"
              error={fieldErrorOf(apiError, 'cnae')}
              onChange={(codigo, descricao) => {
                setCnae(codigo);
                setDescricaoCnae(descricao);
              }}
            />
            {/* Texto, e nao campo numerico: o campo numerico do navegador devolve o
              ponto como decimal e nao aceita a virgula que o restante da tela usa. */}
            <MoneyField
              label="Capital social (R$)"
              value={shareCapital}
              error={erroCapital ?? fieldErrorOf(apiError, 'share_capital')}
              onValueChange={setShareCapital}
            />
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          {!pessoaFisica ? (
            <Field
              label="Data de abertura"
              type="date"
              value={foundedOn}
              error={fieldErrorOf(apiError, 'founded_on')}
              onChange={(event) => setFoundedOn(event.target.value)}
            />
          ) : null}
          {!daCorretora ? (
            <Field
              label="Comercial responsavel"
              value={commercialOwner}
              onChange={(event) => setCommercialOwner(event.target.value)}
            />
          ) : null}
        </div>

        {!daCorretora ? (
          <Select
            label="Corretor"
            value={brokerId}
            placeholder="Operacao direta, sem corretor"
            options={(listaCorretores.data?.items ?? []).map((corretor) => ({
              value: corretor.id,
              label: corretor.legal_name,
            }))}
            hint={
              corretorPorPedido
                ? DICA_CORRETOR_POR_PEDIDO
                : 'Apenas corretores ativos aparecem aqui.'
            }
            disabled={corretorPorPedido}
            error={fieldErrorOf(apiError, 'broker_id')}
            onChange={(event) => setBrokerId(event.target.value)}
          />
        ) : null}

        {!pessoaFisica ? (
          <Grupo titulo="Empresa (ficha completa)">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Obs. Fundacao"
                value={obsFundacao}
                onChange={(event) => setObsFundacao(event.target.value)}
              />
              <Field
                label="Ramo de atuacao"
                value={ramo}
                onChange={(event) => setRamo(event.target.value)}
              />
              <Field
                label="Descricao do CNAE"
                value={descricaoCnae}
                hint="Vem da CNAE quando o codigo muda."
                onChange={(event) => setDescricaoCnae(event.target.value)}
              />
              <div className="flex flex-col gap-2">
                <CnaePicker
                  label="CNAEs secundarios"
                  value=""
                  hint="Busque e escolha para acrescentar."
                  error={fieldErrorOf(apiError, 'secondary_cnaes')}
                  onChange={(codigo) => {
                    if (codigo && !secundarios.includes(codigo)) {
                      setSecundarios([...secundarios, codigo]);
                    }
                  }}
                />
                {secundarios.length > 0 ? (
                  <ul
                    className="flex flex-wrap gap-2"
                    aria-label="CNAEs secundarios escolhidos"
                  >
                    {secundarios.map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-1 rounded-full border border-line px-2 py-0.5 text-xs text-ink-800"
                      >
                        {/^\d{7}$/.test(item)
                          ? formatCnae(item)
                          : `${item} (como gravado)`}
                        <button
                          type="button"
                          className="text-ink-500 hover:text-danger-600"
                          aria-label={`Remover o CNAE ${formatCnae(item)}`}
                          onClick={() =>
                            setSecundarios(
                              secundarios.filter((atual) => atual !== item),
                            )
                          }
                        >
                          x
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </Grupo>
        ) : null}

        {!daCorretora ? (
          <Grupo titulo="Cadastro, corretagem e CCG (ficha completa)">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label="Data de validade cadastral"
                type="date"
                value={validadeCadastral}
                error={fieldErrorOf(apiError, 'registration_valid_until')}
                onChange={(event) => setValidadeCadastral(event.target.value)}
              />
              <Field
                label="Data recadastro"
                type="date"
                value={recadastro}
                error={fieldErrorOf(apiError, 'reregistered_on')}
                onChange={(event) => setRecadastro(event.target.value)}
              />
              {!pessoaFisica ? (
                <Select
                  label="Cadastro na Receita"
                  value={receita}
                  placeholder="Nao informado"
                  options={situacoesNaReceita}
                  error={fieldErrorOf(apiError, 'registration_situation')}
                  onChange={(event) => setReceita(event.target.value)}
                />
              ) : null}
              <Field
                label="Comissao do tomador (%)"
                inputMode="decimal"
                value={comissao}
                error={erroComissao ?? fieldErrorOf(apiError, 'commission_bps')}
                hint="Vazio significa sem comissao diferenciada."
                onChange={(event) => setComissao(event.target.value)}
              />
              <Field
                label="Validade da nomeacao"
                type="date"
                value={validadeNomeacao}
                onChange={(event) => setValidadeNomeacao(event.target.value)}
              />
              <Select
                label="Instrumento da nomeacao"
                value={instrumento}
                placeholder="Nao informado"
                options={instrumentosDeNomeacao}
                onChange={(event) => setInstrumento(event.target.value)}
              />
              <Select
                label="Ramo ou CNAE restrito"
                value={ramoRestrito}
                placeholder="Nao informado"
                options={marcaTriplice}
                onChange={(event) => setRamoRestrito(event.target.value)}
              />
              <Select
                label="Direito privado"
                value={direitoPrivado}
                placeholder="Nao informado"
                options={marcaTriplice}
                onChange={(event) => setDireitoPrivado(event.target.value)}
              />
              <Field
                label="Classe de pagamento equivalente"
                value={classePagamento}
                onChange={(event) => setClassePagamento(event.target.value)}
              />
              <Field
                label="Ultima avaliacao de sinistros"
                type="date"
                value={ultimaSinistros}
                onChange={(event) => setUltimaSinistros(event.target.value)}
              />
              <Field
                label="Percentual do faturamento anual (%)"
                inputMode="decimal"
                value={percentualFaturamento}
                error={
                  erroPercentual ??
                  fieldErrorOf(apiError, 'annual_billing_percentage_bps')
                }
                onChange={(event) => setPercentualFaturamento(event.target.value)}
              />
            </div>
            <Marcador
              label="Parcelamento anual habilitado"
              checked={parcelamentoAnual}
              onChange={setParcelamentoAnual}
            />
            <Textarea
              label="Clausulado especifico da CCG"
              value={clausulaCcg}
              onChange={(event) => setClausulaCcg(event.target.value)}
            />
            <Marcador
              label="Usa conjuge na CCG"
              checked={usaConjuge}
              onChange={setUsaConjuge}
            />
          </Grupo>
        ) : null}

        <AddressFields value={address} onChange={setAddress} withCityCode />

        <Textarea
          label="Observacoes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </fieldset>

      {!readOnly ? (
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={submitting} disabled={valorInvalido}>
            {holder ? 'Salvar alteracoes' : 'Cadastrar'}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
